/**
 * Analytics Service for FridaySwipe
 * Production-ready tracking with privacy compliance
 */

import { getAPIRoot } from './api';

class AnalyticsService {
  constructor() {
    this.queue = [];
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 seconds
    this.sessionId = this.generateSessionId();
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Start batch processing
    this.startBatchProcessor();
    
    // Track page visibility
    this.trackVisibility();
  }

  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track an event with properties
   */
  track(eventName, properties = {}) {
    const event = {
      event: eventName,
      properties: {
        ...properties,
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        platform: this.getPlatform(),
        screen_size: this.getScreenSize()
      }
    };

    // Add to queue
    this.queue.push(event);

    // Log in development
    if (!this.isProduction) {
      console.log('📊 Analytics:', eventName, properties);
    }

    // Flush if queue is full
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Track card interactions
   */
  trackCardInteraction(action, profile) {
    const metrics = {
      action,
      user_id: profile.id,
      has_photo: !!profile.profile_image_url,
      bio_length: profile.bio?.length || 0,
      interests_count: profile.interests?.length || 0,
      vibe: profile.vibe_tags?.[0] || 'unknown',
      city: profile.city,
      session_swipe_count: this.getSessionSwipeCount()
    };

    this.track('card_interaction', metrics);
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metric, value) {
    this.track('performance', {
      metric,
      value,
      user_agent: navigator.userAgent
    });
  }

  /**
   * Track errors
   */
  trackError(error, context = {}) {
    this.track('error', {
      message: error.message,
      stack: error.stack,
      ...context
    });

    // Also send to error monitoring service
    if (this.isProduction && window.Sentry) {
      window.Sentry.captureException(error, { extra: context });
    }
  }

  /**
   * Batch processor
   */
  startBatchProcessor() {
    setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush(true);
    });
  }

  /**
   * Send events to analytics endpoint
   */
  async flush(useBeacon = false) {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    const payload = {
      events,
      user_id: this.getUserId(),
      session_id: this.sessionId
    };

    try {
      const API_BASE = getAPIRoot();
      if (useBeacon && navigator.sendBeacon) {
        // Use sendBeacon for reliability on page unload
        navigator.sendBeacon(`${API_BASE}/analytics`, JSON.stringify(payload));
      } else {
        // Regular fetch
        await fetch(`${API_BASE}/analytics`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
    } catch (error) {
      // Re-queue events on failure
      this.queue.unshift(...events);
    }
  }

  /**
   * Track page visibility
   */
  trackVisibility() {
    let hiddenTime = null;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        hiddenTime = Date.now();
      } else if (hiddenTime) {
        const duration = Date.now() - hiddenTime;
        this.track('app_background_time', { duration });
        hiddenTime = null;
      }
    });
  }

  /**
   * Helper methods
   */
  getPlatform() {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'web';
  }

  getScreenSize() {
    return `${window.screen.width}x${window.screen.height}`;
  }

  getUserId() {
    // Get from auth context
    return localStorage.getItem('userId') || 'anonymous';
  }

  getSessionSwipeCount() {
    const count = parseInt(sessionStorage.getItem('swipe_count') || '0');
    return count;
  }

  incrementSwipeCount() {
    const count = this.getSessionSwipeCount() + 1;
    sessionStorage.setItem('swipe_count', count.toString());
    return count;
  }

  /**
   * FSwipe-specific metrics
   */
  trackSwipeMetrics() {
    const session = {
      total_swipes: this.getSessionSwipeCount(),
      likes: parseInt(sessionStorage.getItem('like_count') || '0'),
      passes: parseInt(sessionStorage.getItem('pass_count') || '0'),
      session_duration: Date.now() - parseInt(sessionStorage.getItem('session_start') || Date.now())
    };

    this.track('session_metrics', session);
  }

  /**
   * A/B Testing support
   */
  getVariant(experimentName) {
    const variants = JSON.parse(localStorage.getItem('experiments') || '{}');
    return variants[experimentName] || 'control';
  }

  trackExperiment(experimentName, variant, event) {
    this.track('experiment_event', {
      experiment: experimentName,
      variant,
      event
    });
  }
}

// Singleton instance
export const analyticsService = new AnalyticsService();

// Performance monitoring
export const measureCardRender = () => {
  const start = performance.now();
  return () => {
    const duration = performance.now() - start;
    analyticsService.trackPerformance('card_render_time', duration);
  };
};

// Error boundary integration
export const trackErrorBoundary = (error, errorInfo) => {
  analyticsService.trackError(error, {
    component_stack: errorInfo.componentStack,
    error_boundary: true
  });
};
